import { useState, useEffect } from "react";
import { Schedule } from "../../../../models/offers";

type SchedulePickerProps = {
    schedule?: Schedule;
    onChange: (schedule: Schedule) => void;
};

const ALL_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS_12H = Array.from({ length: 24 }, (_, i) => {
    const period = i < 12 ? "am" : "pm";
    const hour = i % 12 === 0 ? 12 : i % 12;
    return { value: i, label: `${hour} ${period}` };
});

// Add special option for full day
const FULL_DAY_OPTION = { value: -1, label: "24 Hours" };

export default function SchedulePicker({ schedule, onChange }: SchedulePickerProps) {
    const [days, setDays] = useState<string[]>(schedule?.days ?? []);
    const [startHour, setStartHour] = useState<number>(schedule?.hours[0] ?? 0);
    const [endHour, setEndHour] = useState<number>(schedule?.hours[1] ?? 0);

    useEffect(() => {
        setDays(schedule?.days ?? []);
        setStartHour(schedule?.hours[0] ?? 0);
        setEndHour(schedule?.hours[1] ?? 0);
    }, [schedule]);

    const toggleDay = (day: string) => {
        const newDays = days.includes(day) ? days.filter(d => d !== day) : [...days, day];
        setDays(newDays);
        onChange({ days: newDays, hours: [startHour, endHour] });
    };

    const handleStartHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = Number(e.target.value);
        if (val === -1) {
            setStartHour(0);
            setEndHour(0);
            onChange({ days, hours: [0, 0] });
        } else {
            setStartHour(val);
            onChange({ days, hours: [val, endHour] });
        }
    };

    const handleEndHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = Number(e.target.value);
        if (val === -1) {
            setStartHour(0);
            setEndHour(0);
            onChange({ days, hours: [0, 0] });
        } else {
            setEndHour(val);
            onChange({ days, hours: [startHour, val] });
        }
    };

    const isFullDay = startHour === 0 && endHour === 0;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex gap-2 flex-wrap">
                {ALL_DAYS.map(day => (
                    <button
                        key={day}
                        onClick={() => toggleDay(day)}
                        className={`px-2 py-1 rounded border ${days.includes(day) ? "bg-[#FF7A00] text-white" : "bg-gray-200"
                            }`}
                    >
                        {day}
                    </button>
                ))}
            </div>
            <div className="flex gap-4 items-center">
                <div className="flex flex-col">
                    <label className="text-sm font-medium">Start Hour</label>
                    <select
                        value={isFullDay ? -1 : startHour}
                        onChange={handleStartHourChange}
                        className="border rounded p-2"
                    >
                        <option value={FULL_DAY_OPTION.value}>{FULL_DAY_OPTION.label}</option>
                        {HOURS_12H.map(h => (
                            <option key={h.value} value={h.value}>{h.label}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col">
                    <label className="text-sm font-medium">End Hour</label>
                    <select
                        value={isFullDay ? -1 : endHour}
                        onChange={handleEndHourChange}
                        className="border rounded p-2"
                    >
                        <option value={FULL_DAY_OPTION.value}>{FULL_DAY_OPTION.label}</option>
                        {HOURS_12H.map(h => (
                            <option key={h.value} value={h.value}>{h.label}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}
